import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { retry, timer } from 'rxjs';
import { AppInsightsService } from 'src/app/core/services/app-insights.service';

/**
 * Transient HTTP error codes that are candidates for retry
 * - 502 Bad Gateway: upstream server error, often transient
 * - 503 Service Unavailable: server temporarily overloaded or in maintenance
 * - 504 Gateway Timeout: upstream server didn't respond in time (cold start)
 */
const TRANSIENT_ERROR_CODES = [502, 503, 504];

/**
 * Maximum number of retry attempts before giving up
 */
const MAX_RETRIES = 2;

/**
 * Base delay in milliseconds for exponential backoff
 * Retry 1: 1000ms, Retry 2: 2000ms
 */
const BASE_DELAY_MS = 1000;

/**
 * Check if an HTTP error is transient and should be retried
 */
function isTransientError(error: unknown): boolean {
  return error instanceof HttpErrorResponse && 
         TRANSIENT_ERROR_CODES.includes(error.status);
}

/**
 * HTTP Retry Interceptor
 * 
 * Automatically retries failed HTTP requests for transient server errors
 * (502, 503, 504) using exponential backoff. This is particularly useful
 * for mobile PWA scenarios where:
 * - The backend may be cold-starting after idle periods
 * - Network conditions can cause temporary timeouts
 * - The app resumes from background and needs to refresh data
 * 
 * Retry Strategy:
 * - Only retries GET requests (safe, idempotent operations)
 * - Uses exponential backoff: 1s, then 2s delay
 * - Maximum 2 retry attempts before propagating the error
 * - Logs retry attempts to Application Insights for monitoring
 * 
 * @example
 * // In app.config.ts, add before httpErrorInterceptor:
 * provideHttpClient(
 *   withInterceptors([httpRetryInterceptor, httpErrorInterceptor])
 * )
 */
export const httpRetryInterceptor: HttpInterceptorFn = (req, next) => {
  const appInsightsService = inject(AppInsightsService);

  // Only retry GET requests - POST/PUT/DELETE are not safe to retry
  if (req.method !== 'GET') {
    return next(req);
  }

  return next(req).pipe(
    retry({
      count: MAX_RETRIES,
      delay: (error, retryCount) => {
        // Only retry transient errors
        if (!isTransientError(error)) {
          throw error;
        }

        const delayMs = BASE_DELAY_MS * retryCount;
        
        // Log retry attempt to Application Insights
        appInsightsService.trackTrace(
          `HTTP retry attempt ${retryCount}/${MAX_RETRIES}`,
          {
            url: req.url,
            method: req.method,
            errorStatus: error.status,
            errorMessage: error.message,
            delayMs: delayMs,
            retryCount: retryCount
          }
        );

        console.warn(
          `[httpRetryInterceptor] Transient error ${error.status} on ${req.url}, ` +
          `retrying in ${delayMs}ms (attempt ${retryCount}/${MAX_RETRIES})`
        );

        return timer(delayMs);
      }
    })
  );
};
